/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useMemo } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { BlockType, JTLDescription, PushDescriptionRequest, PushDescriptionResponse } from '@/lib/api/descriptions'
import { CONTENT_FIELDS, type ContentFieldKey, getFieldValue } from '@/components/products/v2/tabs/content/channels/channel-constants'
import { BULLET_KEYS, type BulletKey } from '@/components/products/v2/tabs/content/channels/channel-constants'
import { computeInheritanceMap, countOverrides, type InheritanceMap } from '@/hooks/useInheritance'
import { useBulletsState } from '@/hooks/useBulletsState'

export interface UseChannelEditorOptions {
  channelDescription: JTLDescription | undefined
  masterDescription: JTLDescription | undefined
  channelId: string
  selectedLang: string
  pushMutation: UseMutationResult<PushDescriptionResponse, Error, PushDescriptionRequest>
  productId?: string
  includeBullets?: boolean
}

export interface UseChannelEditorReturn {
  editFields: Record<ContentFieldKey, string>
  bulletFields: Record<BulletKey, string>
  bulletsAvailable: boolean
  mappedBulletKeys: BulletKey[]
  keywordsMapped: boolean
  keywordsValue: string
  updateKeywords: (value: string) => void
  inheritanceMap: InheritanceMap
  overrideCount: number
  isDirty: boolean
  isSaving: boolean
  updateField: (key: ContentFieldKey, value: string) => void
  updateBullet: (key: BulletKey, value: string) => void
  toggleOverride: (key: ContentFieldKey) => void
  save: () => Promise<boolean>
  discard: () => void
}

const FIELD_TO_BLOCK_TYPE: Record<ContentFieldKey, string> = {
  Name: 'title',
  Description: 'description',
  ShortDescription: 'short_description',
  SeoTitleTag: 'seo_meta',
  SeoMetaDescription: 'seo_meta',
  SeoMetaKeywords: 'seo_meta',
  SeoPath: 'seo_meta',
}

const SEO_FIELDS: ContentFieldKey[] = ['SeoTitleTag', 'SeoMetaDescription', 'SeoMetaKeywords', 'SeoPath']

function buildInitialFields(desc: JTLDescription | undefined): Record<ContentFieldKey, string> {
  return Object.fromEntries(CONTENT_FIELDS.map((f) => [f, getFieldValue(desc, f)])) as Record<ContentFieldKey, string>
}

async function pushDirtyBullets(
  bulletFields: Record<BulletKey, string>,
  bulletsDirty: Record<BulletKey, boolean>,
  channelId: string,
  selectedLang: string,
  pushMutation: UseMutationResult<PushDescriptionResponse, Error, PushDescriptionRequest>,
  keywordsDirty: boolean,
  keywordsValue: string,
): Promise<void> {
  for (const key of BULLET_KEYS) {
    if (!bulletsDirty[key]) continue
    await pushMutation.mutateAsync({
      sales_channel_id: channelId,
      language_iso: selectedLang,
      block_type: key,
      content: bulletFields[key],
    })
  }
  if (keywordsDirty) {
    await pushMutation.mutateAsync({
      sales_channel_id: channelId,
      language_iso: selectedLang,
      block_type: 'SeoMetaKeywords' as BlockType,
      content: keywordsValue,
    })
  }
}

async function executeSave(
  fields: Record<ContentFieldKey, string>,
  overrideMap: Record<ContentFieldKey, boolean>,
  bulletFields: Record<BulletKey, string>,
  bulletsDirty: Record<BulletKey, boolean>,
  channelId: string,
  selectedLang: string,
  pushMutation: UseMutationResult<PushDescriptionResponse, Error, PushDescriptionRequest>,
  t: (key: string) => string,
  keywordsDirty: boolean,
  keywordsValue: string,
): Promise<boolean> {
  try {
    const seoOverridden = SEO_FIELDS.some((f) => overrideMap[f])
    const handled = new Set<string>()
    for (const field of CONTENT_FIELDS) {
      if (!overrideMap[field]) continue
      const blockType = FIELD_TO_BLOCK_TYPE[field]
      if (blockType === 'seo_meta') {
        if (handled.has('seo_meta')) continue
        handled.add('seo_meta')
        if (!seoOverridden) continue
        const content = JSON.stringify({
          title: fields.SeoTitleTag,
          description: fields.SeoMetaDescription,
          keywords: fields.SeoMetaKeywords,
          path: fields.SeoPath,
        })
        await pushMutation.mutateAsync({ sales_channel_id: channelId, language_iso: selectedLang, block_type: 'seo_meta', content })
      } else {
        await pushMutation.mutateAsync({ sales_channel_id: channelId, language_iso: selectedLang, block_type: blockType as BlockType, content: fields[field] })
      }
    }
    await pushDirtyBullets(bulletFields, bulletsDirty, channelId, selectedLang, pushMutation, keywordsDirty, keywordsValue)
    toast.success(t('productContent.channels.pushSuccess'))
    return true
  } catch {
    toast.error(t('productContent.channels.pushError'))
    return false
  }
}

export function useChannelEditor({
  channelDescription,
  masterDescription,
  channelId,
  selectedLang,
  pushMutation,
  productId,
  includeBullets = false,
}: UseChannelEditorOptions): UseChannelEditorReturn {
  const { t } = useTranslation()
  const [editFields, setEditFields] = useState<Record<ContentFieldKey, string>>(() => buildInitialFields(channelDescription))
  const serverInheritance = useMemo(() => computeInheritanceMap(masterDescription, channelDescription), [masterDescription, channelDescription])
  const [overrideMap, setOverrideMap] = useState<Record<ContentFieldKey, boolean>>(() =>
    Object.fromEntries(CONTENT_FIELDS.map((f) => [f, serverInheritance[f] === 'overridden'])) as Record<ContentFieldKey, boolean>,
  )

  const bullets = useBulletsState({ productId, channelId, includeBullets })

  useEffect(() => {
    const initial = buildInitialFields(channelDescription)
    setEditFields(initial)
    const newInheritance = computeInheritanceMap(masterDescription, channelDescription)
    setOverrideMap(Object.fromEntries(CONTENT_FIELDS.map((f) => [f, newInheritance[f] === 'overridden'])) as Record<ContentFieldKey, boolean>)
  }, [channelId, selectedLang]) // eslint-disable-line react-hooks/exhaustive-deps

  const inheritanceMap: InheritanceMap = useMemo(
    () => Object.fromEntries(CONTENT_FIELDS.map((f) => [f, overrideMap[f] ? 'overridden' : 'inherited'])) as InheritanceMap,
    [overrideMap],
  )
  const overrideCount = useMemo(() => countOverrides(inheritanceMap), [inheritanceMap])
  const isDirty = useMemo(() => {
    const fieldsDirty = CONTENT_FIELDS.some((f) => editFields[f] !== getFieldValue(channelDescription, f))
    return fieldsDirty || bullets.anyDirty
  }, [editFields, channelDescription, bullets.anyDirty])

  const updateField = (key: ContentFieldKey, value: string) => {
    setEditFields((prev) => ({ ...prev, [key]: value }))
    setOverrideMap((prev) => ({ ...prev, [key]: true }))
  }

  const toggleOverride = (key: ContentFieldKey) => {
    if (overrideMap[key]) {
      setEditFields((prev) => ({ ...prev, [key]: getFieldValue(masterDescription, key) }))
      setOverrideMap((prev) => ({ ...prev, [key]: false }))
    } else {
      setOverrideMap((prev) => ({ ...prev, [key]: true }))
    }
  }

  const discard = () => {
    const initial = buildInitialFields(channelDescription)
    setEditFields(initial)
    const newInheritance = computeInheritanceMap(masterDescription, channelDescription)
    setOverrideMap(
      Object.fromEntries(CONTENT_FIELDS.map((f) => [f, newInheritance[f] === 'overridden'])) as Record<ContentFieldKey, boolean>,
    )
    bullets.resetBullets()
  }

  const save = () =>
    executeSave(editFields, overrideMap, bullets.bulletFields, bullets.bulletsDirty, channelId, selectedLang, pushMutation, t, bullets.keywordsDirty, bullets.keywordsValue)

  return {
    editFields,
    bulletFields: bullets.bulletFields,
    bulletsAvailable: bullets.bulletsAvailable,
    mappedBulletKeys: bullets.mappedBulletKeys,
    keywordsMapped: bullets.keywordsMapped,
    keywordsValue: bullets.keywordsValue,
    updateKeywords: bullets.updateKeywords,
    inheritanceMap,
    overrideCount,
    isDirty,
    isSaving: pushMutation.isPending,
    updateField,
    updateBullet: bullets.updateBullet,
    toggleOverride,
    save,
    discard,
  }
}
